export interface MaskTemplate {
  pattern: string;
  hasAmPm: boolean;
}

const FORMAT_CHARS = new Set(["d", "M", "y", "H", "h", "m", "s"]);

const DIGIT = /\d/;
const AMPM_CHAR = /[APM]/;
const VALID_CHAR = /[\dAPM]/i;

const isSlot = (ch: string) => ch === "#" || ch === "@";

const isValidChar = (ch: string, hasAmPm: boolean) =>
  DIGIT.test(ch) || (hasAmPm && AMPM_CHAR.test(ch));

/**
 * Convert a display format string into a mask template.
 *
 * Format chars (d, M, y, H, h, m, s) → digit slots (#).
 * A (from AA) → ampm letter slots (@).
 * Everything else → literal separators.
 *
 * Examples:
 *   "dd/MM/yyyy" → "##/##/####"
 *   "hh:mm AA"   → "##:## @@"
 */
export const buildMaskTemplate = (format: string): MaskTemplate => {
  let pattern = "";
  let hasAmPm = false;

  for (const char of format) {
    if (FORMAT_CHARS.has(char)) {
      pattern += "#";
    } else if (char === "A") {
      pattern += "@";
      hasAmPm = true;
    } else {
      pattern += char;
    }
  }

  return { pattern, hasAmPm };
};

/**
 * Build a range mask template: two single-date templates joined by " - ".
 */
export const buildRangeMaskTemplate = (format: string): MaskTemplate => {
  const single = buildMaskTemplate(format);

  return {
    pattern: `${single.pattern} - ${single.pattern}`,
    hasAmPm: single.hasAmPm,
  };
};

// Tokens like MMM/MMMM produce textual output (month names), not digits.
const TEXTUAL_WHEN_LONG = new Set(["M", "d"]);

/**
 * Check if a format string uses only fixed-width numeric tokens (dd, MM, yyyy, etc).
 * Returns false for single-char tokens (d, M) and textual tokens (MMM, MMMM).
 */
export const isFixedWidthFormat = (format: string): boolean => {
  let i = 0;
  while (i < format.length) {
    const char = format[i];
    if (!FORMAT_CHARS.has(char)) {
      i++;
      continue;
    }

    let count = 0;
    while (i + count < format.length && format[i + count] === char) count++;

    if (count === 1) return false;
    if (count >= 3 && TEXTUAL_WHEN_LONG.has(char)) return false;

    i += count;
  }

  return true;
};

const findDeletionPoint = (prev: string, input: string): number => {
  for (let i = 0; i < prev.length; i++) {
    if (i >= input.length || input[i] !== prev[i]) return i;
  }

  return prev.length - 1;
};

const hasDigitsAfter = (
  text: string,
  pos: number,
  pattern: string
): boolean => {
  for (let i = pos + 1; i < text.length && i < pattern.length; i++) {
    if (isSlot(pattern[i]) && text[i] !== "_") return true;
  }

  return false;
};

const countValidBefore = (
  text: string,
  pos: number,
  hasAmPm: boolean
): number => {
  let count = 0;
  for (let i = 0; i < pos && i < text.length; i++) {
    if (isValidChar(text[i].toUpperCase(), hasAmPm)) count++;
  }

  return count;
};

const extractWithPattern = (text: string, pattern: string): string[] => {
  const chars: string[] = [];
  const upper = text.toUpperCase();
  let pIdx = 0;

  for (let i = 0; i < upper.length; i++) {
    const ch = upper[i];
    while (pIdx < pattern.length && !isSlot(pattern[pIdx])) pIdx++;
    if (pIdx >= pattern.length) break;

    if (pattern[pIdx] === "#" && DIGIT.test(ch)) {
      chars.push(ch);
      pIdx++;
    } else if (pattern[pIdx] === "@" && AMPM_CHAR.test(ch)) {
      chars.push(ch);
      pIdx++;
    }
  }

  return chars;
};

const rebuildFromChars = (validChars: string[], pattern: string): string => {
  let result = "";
  let charIdx = 0;

  for (let i = 0; i < pattern.length && charIdx < validChars.length; i++) {
    if (!isSlot(pattern[i])) continue;

    result += validChars[charIdx];
    charIdx++;

    let nextSlot = i + 1;
    while (nextSlot < pattern.length && !isSlot(pattern[nextSlot])) nextSlot++;

    const hasMoreChars = charIdx < validChars.length;
    const hasMoreSlots = nextSlot < pattern.length;

    if (hasMoreChars || hasMoreSlots) {
      for (let s = i + 1; s < nextSlot; s++) result += pattern[s];
    }

    if (hasMoreChars) i = nextSlot - 1;
  }

  return result;
};

const stripTrailingSeparators = (text: string): string => {
  let end = text.length;
  while (end > 0 && !VALID_CHAR.test(text[end - 1])) end--;

  return text.slice(0, end);
};

const cursorSlotToDisplayPos = (
  result: string,
  cursorSlot: number,
  pattern: string,
  skipSeparators: boolean
): number => {
  if (cursorSlot <= 0) return 0;

  let slotsFound = 0;
  for (let i = 0; i < result.length; i++) {
    if (!VALID_CHAR.test(result[i])) continue;

    slotsFound++;
    if (slotsFound !== cursorSlot) continue;

    let pos = i + 1;
    if (skipSeparators) {
      while (
        pos < result.length &&
        pos < pattern.length &&
        !isSlot(pattern[pos])
      ) {
        pos++;
      }
    }

    return pos;
  }

  return result.length;
};

const isValidForSlot = (ch: string, slotChar: string): boolean =>
  slotChar === "#"
    ? DIGIT.test(ch)
    : slotChar === "@"
      ? AMPM_CHAR.test(ch)
      : false;

/**
 * Apply the mask template to raw input text.
 *
 * - Auto-inserts separators as the user types.
 * - When the mask is full, typing in the middle replaces the digit at cursor.
 * - Backspacing in the middle replaces the digit with '_' placeholder
 *   instead of shifting subsequent digits.
 * - AM/PM auto-completes: typing "A" or "P" expands to "AM" or "PM".
 */
export const applyMask = (
  inputText: string,
  template: MaskTemplate,
  previousText = "",
  nativeCursorPos?: number | null
): { text: string; cursorPosition: number } => {
  const { pattern, hasAmPm } = template;

  if (!inputText) return { text: "", cursorPosition: 0 };

  const isDeleting = inputText.length < previousText.length;

  // When backspacing from a full mask, replace the deleted digit with '_'
  // instead of shifting. This keeps other fields (day/month/year) intact.
  if (isDeleting && previousText.length >= pattern.length) {
    const delPos = findDeletionPoint(previousText, inputText);

    if (
      !hasDigitsAfter(previousText, delPos, pattern) ||
      delPos >= pattern.length
    ) {
      // Deletion at or near the end — fall through to normal rebuild
    } else if (isSlot(pattern[delPos])) {
      return {
        text:
          previousText.substring(0, delPos) +
          "_" +
          previousText.substring(delPos + 1),
        cursorPosition: delPos,
      };
    } else {
      // Separator deleted — restore it
      return { text: previousText, cursorPosition: delPos };
    }
  }

  const totalSlots = [...pattern].filter(isSlot).length;
  const slotPatternPositions = [...pattern].reduce<number[]>(
    (acc, ch, i) => (isSlot(ch) ? [...acc, i] : acc),
    []
  );

  const cursorInInput = nativeCursorPos ?? inputText.length;
  const cursorSlot = countValidBefore(inputText, cursorInInput, hasAmPm);
  const prevChars = extractWithPattern(previousText, pattern);
  let validChars: string[];

  // Replace mode: single char typed on a full mask replaces the digit at cursor.
  const isSingleCharInsert =
    !isDeleting &&
    prevChars.length >= totalSlots &&
    inputText.length - previousText.length === 1;

  if (isSingleCharInsert) {
    const insertSlot = cursorSlot - 1;
    const typedChar = inputText[cursorInInput - 1]?.toUpperCase() ?? "";
    const slotChar =
      slotPatternPositions[insertSlot] != null
        ? pattern[slotPatternPositions[insertSlot]]
        : null;

    if (!slotChar || !isValidForSlot(typedChar, slotChar)) {
      return {
        text: previousText,
        cursorPosition: Math.min(cursorInInput, previousText.length),
      };
    }

    validChars = [...prevChars];
    validChars[insertSlot] = typedChar;
  } else {
    validChars = extractWithPattern(inputText, pattern).slice(0, totalSlots);
  }

  let result = rebuildFromChars(validChars, pattern);

  if (isDeleting) {
    result = stripTrailingSeparators(result);
  }

  if (hasAmPm && !isDeleting) {
    const ampmStart = pattern.indexOf("@");
    if (ampmStart >= 0 && result.length > ampmStart) {
      const ampmPortion = result.slice(ampmStart);
      if (ampmPortion === "A") result = result.slice(0, ampmStart) + "AM";
      else if (ampmPortion === "P") result = result.slice(0, ampmStart) + "PM";
    }
  }

  return {
    text: result,
    cursorPosition: cursorSlotToDisplayPos(
      result,
      cursorSlot,
      pattern,
      !isDeleting
    ),
  };
};
